import defaultPsychologistImage from '@avanta/shared/assets/avatar.svg';
import { Text } from '@avanta/shared/components/Text';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { type Languages, LOCIZE_NS } from '@avanta/shared/constants/common';
import { formatTime, formatUnifiedDateWithMonth } from '@avanta/shared/utils/date';
import { Building, Calendar, ClipboardPen, Clock, IdCard, Info, MapPin, User } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InfoItem } from 'components/InfoItem';
import { AppointmentsReservePayload, AppointmentSteps } from 'pages/AppointmentsReserve/constants';
import { useFetchInsuredCenters } from 'pages/AppointmentsReserve/hooks/useFetchInsuredCenters';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { useUserStore } from 'stores/user';
import { SessionSummarySkeleton } from './SessionSummarySkeleton';

const SummaryTitle = ({ children }: React.PropsWithChildren): React.ReactElement => (
  <Text variant="text-sm-medium" className="text-denim-800">
    {children}
  </Text>
);

export const SessionSummary = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [
    patient,
    psychologist,
    city,
    backofficeCenterId,
    dateTime,
    durationType,
    flow,
    currentStepName,
  ] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.psychologist,
      AppointmentsReservePayload.city,
      AppointmentsReservePayload.backofficeCenterId,
      AppointmentsReservePayload.dateTime,
      AppointmentsReservePayload.durationType,
      AppointmentsReservePayload.flow,
      AppointmentsReservePayload.currentStepName,
    ],
  });
  const {
    values: { speciality },
    centersOptions,
    isLoading,
  } = useFetchInsuredCenters();
  const center = centersOptions.find(
    centerOption => String(centerOption.value) === String(backofficeCenterId)
  );

  const specialityLabel = t(`AppointmentsReserve.Steps.speciality.options.${speciality?.name}`);
  const patientName = [patient?.firstName, patient?.surname].filter(Boolean).join(' ');
  const patientNif = patient?.nif;

  const psychologistName = [psychologist?.name, psychologist?.surname].filter(Boolean).join(' ');
  const appLanguage = useUserStore(state => state.user?.appLanguage);
  const psychologistPresentation = psychologist?.presentations[appLanguage as Languages];

  const showDateTimeInfo = Boolean(dateTime && currentStepName === AppointmentSteps.confirmation);
  const showPatientInfo = Boolean(patientName || patientNif);
  const showDurationType = Boolean(durationType);
  const showServiceInfo =
    Boolean(speciality || showDurationType || city) && currentStepName !== AppointmentSteps.data;
  const showPsychologistInfo =
    (psychologist && currentStepName === AppointmentSteps.confirmation) ||
    flow === FLOW_MODES.search_facultative;

  if (isLoading) {
    return (
      <SessionSummarySkeleton
        sections={{
          dateTime: showDateTimeInfo,
          patient: showPatientInfo,
          service: currentStepName !== AppointmentSteps.data,
          psychologist: Boolean(showPsychologistInfo),
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6 border border-teal-300 rounded-xl w-74 shrink-0">
      {showDateTimeInfo && (
        <>
          <div className="flex flex-col gap-2">
            <InfoItem
              icon={Calendar}
              label={dateTime ? formatUnifiedDateWithMonth(dateTime) : ''}
              labelClassName="text-base font-medium"
            />
            <InfoItem
              icon={Clock}
              label={dateTime ? formatTime(dateTime) : ''}
              labelClassName="text-base"
            />
          </div>
          <Separator className="bg-teal-300" />
        </>
      )}
      {showPatientInfo && (
        <div className="flex flex-col gap-4 mb-2">
          <SummaryTitle>
            {t('AppointmentsReserve.Steps.confirmation.SessionSummary.patientInfo.title')}
          </SummaryTitle>
          <div className="flex flex-col gap-2">
            {patientName && <InfoItem icon={User} label={patientName} />}
            {patientNif && <InfoItem icon={IdCard} label={patientNif} />}
          </div>
        </div>
      )}
      {showServiceInfo && (
        <div className="flex flex-col gap-4">
          <SummaryTitle>
            {t('AppointmentsReserve.Steps.confirmation.SessionSummary.serviceInfo.title')}
          </SummaryTitle>
          <div className="flex flex-col gap-2">
            {speciality && <InfoItem icon={ClipboardPen} label={specialityLabel} />}
            {showDurationType && (
              <InfoItem
                icon={Building}
                label={t(`AppointmentsReserve.Steps.confirmation.contactMethod.in-person`, {
                  duration: durationType,
                })}
              />
            )}
            {city && <InfoItem icon={MapPin} label={city} />}
            {center ? (
              <InfoItem
                icon={Building}
                label={center.label}
                description={center.description}
                alignStart
              />
            ) : null}
          </div>
        </div>
      )}
      {showPsychologistInfo && (
        <>
          <Separator className="bg-teal-300" />
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <img
                className="size-14 shrink-0 rounded-full object-cover"
                src={psychologist?.imageCircular || defaultPsychologistImage}
                alt={psychologistName}
                width={56}
                height={56}
              />
              <div className="flex min-w-0 flex-col">
                <Text variant="text-xs-medium" className="text-gray-400">
                  {t('AppointmentsReserve.Steps.confirmation.licensed.label', {
                    professionalNumber: psychologist?.professionalNumber ?? '',
                  })}
                </Text>
                <Text variant="text-sm-medium" className="text-denim-800">
                  {psychologistName}
                </Text>
                <Text variant="text-sm-regular" className="text-denim-800 mt-4 line-clamp-2">
                  {psychologistPresentation ?? ''}
                </Text>
              </div>
            </div>
            {(flow === FLOW_MODES.regular || flow === FLOW_MODES.search_patient) && (
              <div className="flex gap-2 text-sm leading-4.5 text-gray-400">
                <Info className="size-4 shrink-0 text-teal-600" />
                <Text variant="text-sm-regular" className="text-gray-400">
                  {t('AppointmentsReserve.Steps.confirmation.licensed.description')}
                </Text>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
